import { useCallback, useEffect, useRef } from 'react'
import { useAgentEffect } from '@/app/providers/AgentEffectsContext'
import { useIsAgentCreated } from '@/app/providers/AgentCreatedContext'
import type { AgentEffectKind } from '@/lib/agent-effects/agentEffectsStore'
import { isRowOffscreen } from '@/lib/agent-effects/agentEffectScroll'

/**
 * Origin-aware highlight overlay for a single entity row/card. Supersedes
 * `AgentShimmer`: it reads the typed agent-effects coordinator (create / update
 * / move / delete) and falls back to the legacy per-id "agent created" signal so
 * the create highlight keeps working everywhere during migration.
 *
 * Place it as the FIRST child of the card's real DOM root (same contract as
 * AgentShimmer). The overlay paints the border/wash; a class on the PARENT lets
 * the glow bleed outside an `overflow:hidden` card.
 *
 * Fixes the pre-existing AgentShimmer bug where the parent glow class was never
 * applied on first activation (the ref was read while the overlay was still
 * unmounted): here the class is added/removed by a CALLBACK ref, which runs only
 * once the node is actually attached, so the parent is always available.
 *
 * Reduced motion is handled by the shared `@media (prefers-reduced-motion)` rules
 * on `.agent-shimmer-*` (the overlay degrades to a static, motion-free end state).
 * Positional motion (enter/move/exit) is the VIEW's responsibility (AnimatePresence
 * / layout); this component is the in-place highlight only.
 */

const PARENT_GLOW_CLASS = 'agent-shimmer-active'
/** Applied to the parent while a delete is confirmed: disables interaction + dims. */
const DELETING_CLASS = 'agent-fx-deleting'

export interface AgentEffectProps {
  resourceType: string
  objectId: string | number | null | undefined
  /** Stable logical occurrence id (e.g. a recurring calendar instance). */
  instanceId?: string
  /**
   * When true, the FIRST time an effect activates for this row, scroll it into
   * view IF it is currently off-screen — so an agent create/update of a row the
   * user can't see (e.g. editing a "Z" contact while scrolled to "A") jumps the
   * list down to it. Opt-in (contacts only) so task/calendar lists are unchanged.
   * A delete never scrolls (it would yank the viewport to a vanishing row).
   */
  scrollIntoViewOnActivate?: boolean
}

export function AgentEffect({ resourceType, objectId, instanceId, scrollIntoViewOnActivate }: AgentEffectProps) {
  const typed = useAgentEffect(resourceType, objectId, instanceId)
  const legacyCreated = useIsAgentCreated(objectId)
  const kind: AgentEffectKind | null = typed?.kind ?? (legacyCreated ? 'created' : null)

  const isDeleting = kind === 'deleted'
  const parentRef = useRef<HTMLElement | null>(null)
  // Callback ref (not an effect) so the parent node is guaranteed attached when we
  // read it — this is the fix for AgentShimmer's first-activation parent-class bug.
  // Re-runs when `isDeleting` flips because the deps change the ref identity.
  const setOverlayRef = useCallback((node: HTMLSpanElement | null) => {
    if (node) {
      const parent = node.parentElement
      parentRef.current = parent
      parent?.classList.add(PARENT_GLOW_CLASS)
      if (isDeleting) parent?.classList.add(DELETING_CLASS)
    } else {
      parentRef.current?.classList.remove(PARENT_GLOW_CLASS, DELETING_CLASS)
      parentRef.current = null
    }
  }, [isDeleting])

  // Scroll an off-screen row into view once per distinct activation. Runs after
  // the callback ref has attached `parentRef`, so the card node is available.
  const scrolledKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!scrollIntoViewOnActivate || !kind || kind === 'deleted') return
    const node = parentRef.current
    if (!node) return
    // One scroll per distinct effect (effectId), so a re-render never re-yanks.
    const activationKey = typed?.effectId ?? `${resourceType}:${String(objectId)}:${kind}`
    if (scrolledKeyRef.current === activationKey) return
    scrolledKeyRef.current = activationKey
    const vh = window.innerHeight || document.documentElement.clientHeight
    if (!isRowOffscreen(node.getBoundingClientRect(), vh)) return
    const prefersReduced = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    node.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'center' })
  }, [scrollIntoViewOnActivate, kind, typed?.effectId, resourceType, objectId])

  if (!kind) return null

  return (
    <span ref={setOverlayRef} aria-hidden data-agent-fx-kind={kind} className="agent-shimmer-overlay">
      <span className="agent-shimmer-wash" />
    </span>
  )
}
